import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MODULES } from '../utils/permissions';

interface UseModuleAccessReturn {
  canAccessModule: (module: string) => boolean;
  accessibleModules: () => string[];
  isAWSUser: () => boolean;
}

/**
 * Hook for checking AWS user module access
 * Hub is always accessible for AWS users
 * Other modules require explicit grants in aws_accessible_modules
 */
export const useModuleAccess = (): UseModuleAccessReturn => {
  const { user } = useAuth();

  /**
   * Check if user can access a specific module
   * - Hub is always accessible for AWS users
   * - Other modules require explicit grants
   * - Non-AWS users return false (they use permissions instead)
   */
  const canAccessModule = useCallback((module: string): boolean => {
    if (!user) return false;

    // Only AWS users have module-based access
    if (user.role !== 'aws') return false;

    // Hub is always accessible for AWS users
    if (module === MODULES.HUB) return true;

    // Check if module is in aws_accessible_modules
    if (!user.aws_accessible_modules || !Array.isArray(user.aws_accessible_modules)) {
      return false;
    }

    return user.aws_accessible_modules.includes(module);
  }, [user]);

  /**
   * Get all accessible modules for AWS user
   */
  const accessibleModules = useCallback((): string[] => {
    if (!user || user.role !== 'aws') return [];

    // Hub is always included
    const modules: string[] = [MODULES.HUB];

    // Add other accessible modules
    if (user.aws_accessible_modules && Array.isArray(user.aws_accessible_modules)) {
      modules.push(...user.aws_accessible_modules);
    }

    return modules;
  }, [user]);

  /**
   * Check if user is AWS user
   */
  const isAWSUser = useCallback((): boolean => {
    return user?.role === 'aws';
  }, [user]);

  return {
    canAccessModule,
    accessibleModules,
    isAWSUser
  };
};
